import type { CSSProperties } from 'react'
import type { AssetBreakdownRow } from '@/services/fixedAssets'
import type { FieldDefinition, FieldOption } from '@/tableControls'
import { parseMoney } from './fixedAssetsFormat'

export const FIXED_ASSETS_ALLOCATE_BREAKDOWN_PAGE_KEY = 'fixed-assets-allocate-breakdown'

export const BREAKDOWN_DEFAULT_WIDTHS: Record<string, number> = {
  name: 200,
  expense_type: 100,
  location: 120,
  expense: 110,
  true_up: 110,
  accum_through: 160,
  nbv: 100,
}

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export const PINNED_COLUMNS = ['name'] as const

export const BREAKDOWN_COLUMN_KEYS = [
  'name',
  'expense_type',
  'location',
  'expense',
  'true_up',
  'accum_through',
  'nbv',
] as const

export type BreakdownColumnKey = (typeof BREAKDOWN_COLUMN_KEYS)[number]

export const BREAKDOWN_COLUMN_LABELS: Record<BreakdownColumnKey, string> = {
  name: 'Asset',
  expense_type: 'Type',
  location: 'Location',
  expense: 'Month expense',
  true_up: 'True-up',
  accum_through: 'Accumulated through month',
  nbv: 'NBV',
}

const SORTABLE_COLUMN_KEYS = new Set<BreakdownColumnKey>(BREAKDOWN_COLUMN_KEYS)

export const BREAKDOWN_DEFAULT_SORT = [{ field: 'expense', direction: 'desc' as const }]

const EXPENSE_TYPE_OPTIONS: FieldOption[] = [
  { value: 'depreciation', label: 'Depreciation' },
  { value: 'amortization', label: 'Amortization' },
]

function expenseTypeLabel(expenseType: string): string {
  return expenseType === 'amortization' ? 'Amortization' : 'Depreciation'
}

export const BREAKDOWN_FIELD_DEFINITIONS: FieldDefinition[] = BREAKDOWN_COLUMN_KEYS.map((key) => {
  if (key === 'expense_type') {
    return {
      key,
      label: BREAKDOWN_COLUMN_LABELS[key],
      type: 'text',
      sortable: true,
      accessor: (row: unknown) => expenseTypeLabel((row as AssetBreakdownRow).expense_type),
    }
  }
  if (
    key === 'expense' ||
    key === 'true_up' ||
    key === 'accum_through' ||
    key === 'nbv'
  ) {
    return {
      key,
      label: BREAKDOWN_COLUMN_LABELS[key],
      type: 'number',
      sortable: true,
      accessor: (row: unknown) => parseMoney((row as AssetBreakdownRow)[key]),
    }
  }
  return {
    key,
    label: BREAKDOWN_COLUMN_LABELS[key],
    type: 'text',
    sortable: SORTABLE_COLUMN_KEYS.has(key),
    accessor: (row: unknown) => {
      const value = (row as AssetBreakdownRow)[key]
      return typeof value === 'string' ? value.toLowerCase() : value
    },
  }
})

export function isBreakdownColumnSortable(key: string): boolean {
  return SORTABLE_COLUMN_KEYS.has(key as BreakdownColumnKey)
}

export function isBreakdownPinnedColumn(columnKey: string): boolean {
  return PINNED_COLUMNS.includes(columnKey as (typeof PINNED_COLUMNS)[number])
}

export function breakdownPinnedHeaderClass(columnKey: string): string {
  if (!isBreakdownPinnedColumn(columnKey)) return ''
  return 'table-sticky-pinned table-sticky-cell table-locked-edge'
}

export function breakdownPinnedCellClass(columnKey: string): string {
  if (!isBreakdownPinnedColumn(columnKey)) return ''
  return 'table-sticky-pinned table-sticky-cell table-locked-edge'
}

export function buildFixedAssetsBreakdownFilterFields(
  locationOptions: FieldOption[],
): FieldDefinition[] {
  return [
    {
      key: 'name',
      label: 'Asset',
      type: 'text',
      filterable: true,
      accessor: (row) => (row as AssetBreakdownRow).name ?? '',
    },
    {
      key: 'expense_type',
      label: 'Type',
      type: 'select',
      filterable: true,
      options: EXPENSE_TYPE_OPTIONS,
      accessor: (row) => (row as AssetBreakdownRow).expense_type,
    },
    {
      key: 'location',
      label: 'Location',
      type: 'multiSelect',
      filterable: true,
      options: locationOptions,
      accessor: (row) => (row as AssetBreakdownRow).location,
    },
    {
      key: 'expense',
      label: 'Month expense',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as AssetBreakdownRow).expense),
    },
    {
      key: 'true_up',
      label: 'True-up',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as AssetBreakdownRow).true_up),
    },
    {
      key: 'has_true_up',
      label: 'Has true-up',
      type: 'boolean',
      filterable: true,
      accessor: (row) => Math.abs(parseMoney((row as AssetBreakdownRow).true_up)) > 0.005,
    },
    {
      key: 'accum_through',
      label: 'Accumulated through month',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as AssetBreakdownRow).accum_through),
    },
    {
      key: 'nbv',
      label: 'NBV',
      type: 'number',
      filterable: true,
      accessor: (row) => parseMoney((row as AssetBreakdownRow).nbv),
    },
  ]
}
